import { HttpParams } from '@angular/common/http';
import type { IQueryParam } from 'modelos';

/**
 * Arma los `HttpParams` de un listado.
 *
 * Mismo contrato que `HelperService.getQueryParams()` de IRIX: el backend no se
 * toca (ADR-008), así que la forma de la query tampoco.
 *
 *   GET /luminarias?filter={...}&limit=&page=&sort=&select=&populate=
 *
 * **El total no se pide salvo que se pida.** Por defecto viaja
 * `excludeTotalCount=true`. api-datos corre el `countDocuments` en paralelo al
 * `find`, con el mismo tope de 45 s, y si el conteo falla **falla el listado
 * entero**: sobre eventos, reportes o luminarias con filtros fuera del índice,
 * contar es lo que da el error, no traer la página. IRIX lo resolvía a mano en
 * cada llamada; acá es la regla.
 *
 * Quien muestra el total (el paginador de un listado, un contador) lo pide
 * explícito con `excludeTotalCount: false` — o `onlyTotalCount: true` si sólo
 * quiere el número. Sin eso `totalCount` llega `undefined`, y la tabla pagina
 * igual: "siguiente" se apaga cuando la página vuelve incompleta.
 */
export function queryParams(query?: IQueryParam): HttpParams {
  let params = new HttpParams();
  const conTotal = query?.excludeTotalCount === false || !!query?.onlyTotalCount;
  if (!conTotal) params = params.set('excludeTotalCount', true);
  if (!query) return params;

  const conocidas = [
    'page',
    'limit',
    'sort',
    'populate',
    'select',
    'filter',
    'includeChildren',
    'excludeTotalCount',
    'unlimited',
  ];

  if (query.page) params = params.set('page', String(query.page));
  if (query.limit) params = params.set('limit', String(query.limit));
  // `limit: 0` quiere decir "todos", pero api-datos lo toma como falsy y aplica
  // el tope por defecto de 1000: el mapa dibujaba 1.000 de 24.000 luminarias y el
  // importador comparaba contra un parque recortado, sin ningún error. El único
  // "sin tope" que entiende es `unlimited`.
  if (query.limit === 0 || query.limit === '0' || query.unlimited) {
    params = params.set('unlimited', true);
  }
  if (query.sort) params = params.set('sort', query.sort);
  if (query.populate) params = params.set('populate', query.populate);
  if (query.select) params = params.set('select', query.select);
  if (query.filter) params = params.set('filter', query.filter);
  if (query.includeChildren) params = params.set('includeChildren', true);

  // Cualquier otra clave viaja tal cual (los listados agregan filtros propios).
  for (const clave of Object.keys(query)) {
    if (conocidas.includes(clave)) continue;
    const valor = (query as Record<string, unknown>)[clave];
    if (valor === undefined || valor === null) continue;
    params = params.set(clave, String(valor));
  }

  return params;
}

/**
 * `$in` con miles de ids revienta la URL con un **414**.
 *
 * `gestion-api-gestion` **no** publica `POST /<entidad>/filter` (sólo lo usa él
 * mismo contra api-datos), así que desde el front no hay forma de mandar el
 * filtro en el body: la única defensa es partir la lista con `enTramos` y pedir
 * un tramo por request.
 */
export const TOPE_IDS_EN_URL = 200;

/** `true` si la lista de ids es tan larga que hay que partirla. */
export function demasiadosIds(ids: readonly unknown[] | undefined): boolean {
  return (ids?.length ?? 0) > TOPE_IDS_EN_URL;
}

/** Parte una lista en tramos que entran en una URL. */
export function enTramos<T>(lista: readonly T[], tamano = TOPE_IDS_EN_URL): T[][] {
  const tramos: T[][] = [];
  for (let i = 0; i < lista.length; i += tamano) tramos.push(lista.slice(i, i + tamano));
  return tramos;
}
